import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface NewInspectionConflictDialogProps {
  open: boolean
  loading?: boolean
  onStartEmpty: () => void
  onDeleteAndStartNew: () => void
  onCancel: () => void
}

/**
 * Shown when the user clicks "New Inspection" and an inspection already
 * exists for the current claim. Lets them either keep the existing
 * inspection and start a separate blank one, or delete it and start fresh.
 */
export function NewInspectionConflictDialog({
  open,
  loading = false,
  onStartEmpty,
  onDeleteAndStartNew,
  onCancel,
}: NewInspectionConflictDialogProps) {
  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 z-[400] bg-black/40 backdrop-blur-[2px]"
        onClick={loading ? undefined : onCancel}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-inspection-conflict-title"
        className={cn(
          'fixed z-[401] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-sm bg-[var(--surface)] rounded-[var(--radius)]',
          'border border-[var(--border)] shadow-2xl animate-fade-in',
          'p-6'
        )}
      >
        <h2
          id="new-inspection-conflict-title"
          className="text-[15px] font-bold text-[var(--text)] mb-2"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {getMessage('inspection.confirm.delete_claim_title')}
        </h2>

        <p className="text-[12px] text-[var(--text-dim)] font-mono leading-relaxed mb-6">
          {getMessage('inspection.confirm.delete_claim_body')}
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={onDeleteAndStartNew}
            disabled={loading}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-5 py-[9px] rounded-[var(--radius-sm)]',
              'text-white text-[12px] font-medium border border-transparent',
              'transition-all duration-[180ms] hover:opacity-90 cursor-pointer',
              loading && 'opacity-70 cursor-not-allowed'
            )}
            style={{ background: 'linear-gradient(135deg, var(--accent), #2563eb)' }}
          >
            {getMessage('inspection.confirm.delete_claim_confirm')}
          </button>

          <button
            onClick={onStartEmpty}
            disabled={loading}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface2)]',
              'text-[var(--text-dim)] text-[12px] cursor-pointer',
              'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.05)]',
              'transition-all duration-[180ms]',
              loading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {getMessage('inspection.confirm.start_empty_confirm')}
          </button>

          <button
            onClick={onCancel}
            disabled={loading}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface2)]',
              'text-[var(--text-dim)] text-[12px] cursor-pointer',
              'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.05)]',
              'transition-all duration-[180ms]',
              loading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {getMessage('common.button.cancel')}
          </button>
        </div>
      </div>
    </>
  )
}
